/**
 * Pay-period, settlement and invoice math shared by Driver Settlements, Payroll & Invoicing and the GL.
 *
 * Periods are fixed-length windows counted from an anchor Sunday (GET /api/accounting/settings):
 *   - driver pay: bi-weekly, Sunday -> Saturday
 *   - customer invoices: weekly, Sunday -> Saturday
 * Only POSTED manifests are paid or invoiced.
 */
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import { DEFAULT_CUSTOMER, getPricing } from './rateEngine';

export const DEFAULT_SETTINGS = {
  // First pay period starts Sunday Oct 11, 2026. vacationRate = default accrual for employees (4%; 6% after 3 years).
  payPeriod: { lengthDays: 14, anchor: '2026-10-11', vacationRate: 0.04 },
  invoicePeriod: { lengthDays: 7, anchor: '2026-10-11' },
  alerts: { overdueHighDays: 30, upcomingApDays: 7 },
};

const iso = (d) => format(d, 'yyyy-MM-dd');

export const periodStartFor = (date, cfg) => {
  const d = typeof date === 'string' ? parseISO(date.slice(0, 10)) : date;
  const offset = differenceInCalendarDays(d, parseISO(cfg.anchor));
  return iso(addDays(parseISO(cfg.anchor), Math.floor(offset / cfg.lengthDays) * cfg.lengthDays));
};

export const shiftPeriod = (start, n, cfg) => iso(addDays(parseISO(start), n * cfg.lengthDays));

export const periodRange = (start, cfg) => {
  const end = addDays(parseISO(start), cfg.lengthDays - 1);
  return { from: start, to: iso(end), label: `${format(parseISO(start), 'EEE MMM d')} – ${format(end, 'EEE MMM d, yyyy')}` };
};

export const manifestDay = (m) => (m.date || '').slice(0, 10);

export const inPeriod = (day, start, cfg) => {
  const { from, to } = periodRange(start, cfg);
  return day >= from && day <= to;
};

export const settlementId = (periodStart, driverId) => `STL-${periodStart.replace(/-/g, '')}-${driverId}`;

const LOCKED = ['approved', 'processed'];

/**
 * Which pay period a posted manifest is paid in. Normally the period of its own date; if that period's settlement
 * was already approved/processed without it (a back-dated manifest), it rolls into the period it was posted in, or
 * the next period that isn't locked yet.
 */
export const payPeriodOf = (m, saved, cfg) => {
  const lockedWithout = (p) => {
    const s = saved[settlementId(p, m.driver.id)];
    return s && LOCKED.includes(s.status) && !(s.manifestIds || []).includes(m.id);
  };
  let p = periodStartFor(manifestDay(m), cfg);
  if (!lockedWithout(p)) return p;
  const posted = periodStartFor(m.postedAt || manifestDay(m), cfg);
  p = posted > p ? posted : shiftPeriod(p, 1, cfg);
  for (let guard = 0; lockedWithout(p) && guard < 60; guard++) p = shiftPeriod(p, 1, cfg);
  return p;
};

const lineFor = (m, cards, periodStart, cfg) => {
  const t = getPricing(m, cards).totals;
  return {
    manifestId: m.id,
    route: m.route,
    date: manifestDay(m),
    pieces: t.pieces,
    stops: t.stops,
    amount: t.driver,
    backdated: !inPeriod(manifestDay(m), periodStart, cfg),
  };
};

/**
 * One settlement per driver per pay period. `saved` holds persisted state keyed by id
 * (status, adjustments, manifestIds frozen at approval). Server settlements that include `lines` are used as-is.
 */
export const buildSettlements = (manifests, cards, periodStart, saved, cfg) => {
  const byDriver = new Map();
  const row = (driver) => {
    if (!byDriver.has(driver.id)) byDriver.set(driver.id, { driver, posted: [], unposted: [] });
    return byDriver.get(driver.id);
  };

  manifests
    .filter((m) => m.driver?.id)
    .forEach((m) => {
      if (m.status === 'posted') {
        if (payPeriodOf(m, saved, cfg) === periodStart) row(m.driver).posted.push(m);
      } else if (inPeriod(manifestDay(m), periodStart, cfg)) {
        row(m.driver).unposted.push(m);
      }
    });
  // Locked settlements keep exactly the manifests they were approved with.
  Object.values(saved)
    .filter((s) => s.periodStart === periodStart && LOCKED.includes(s.status) && s.driver)
    .forEach((s) => row(s.driver));

  return [...byDriver.values()]
    .map(({ driver, posted, unposted }) => {
      const id = settlementId(periodStart, driver.id);
      const persisted = saved[id] || {};
      const status = persisted.status || 'draft';
      let lines;
      if (persisted.lines) lines = persisted.lines;
      else if (LOCKED.includes(status)) {
        lines = (persisted.manifestIds || []).map((mid) => manifests.find((m) => m.id === mid)).filter(Boolean).map((m) => lineFor(m, cards, periodStart, cfg));
      } else lines = posted.map((m) => lineFor(m, cards, periodStart, cfg));

      // Gross pay only — the accountant finalizes payroll (deductions, remittances) outside the TMS.
      // Employees accrue vacation pay on gross; owner-operators don't.
      const adjustments = persisted.adjustments || [];
      const gross = lines.reduce((s, l) => s + l.amount, 0);
      const adjTotal = adjustments.reduce((s, a) => s + Number(a.amount || 0), 0);
      const grossPay = gross + adjTotal;
      const employmentType = persisted.employmentType || driver.employmentType || 'employee';
      const vacationRate = employmentType === 'employee' ? persisted.vacationRate ?? driver.vacationRate ?? cfg.vacationRate ?? 0.04 : 0;
      return {
        id,
        periodStart,
        driver,
        employmentType,
        status,
        approvedAt: persisted.approvedAt,
        processedAt: persisted.processedAt,
        lines,
        unpostedCount: unposted.length,
        pieces: lines.reduce((s, l) => s + l.pieces, 0),
        gross,
        adjustments,
        adjTotal,
        grossPay,
        vacationRate,
        vacationAccrued: persisted.vacationAccrued ?? Math.round(grossPay * vacationRate * 100) / 100,
      };
    })
    .sort((a, b) => a.driver.name.localeCompare(b.driver.name));
};

/** Customer invoice preview for an invoice period: one line per posted manifest dated in it, taxes on the subtotal. */
export const buildInvoiceDraft = (manifests, cards, periodStart, customer, cfg) => {
  const lines = manifests
    .filter((m) => m.status === 'posted' && inPeriod(manifestDay(m), periodStart, cfg) && (m.customer || DEFAULT_CUSTOMER) === customer)
    .map((m) => {
      const t = getPricing(m, cards).totals;
      return { manifestId: m.id, date: manifestDay(m), route: m.route, pieces: t.pieces, base: t.base, accessorial: t.accessorial, amount: t.customer };
    })
    .sort((a, b) => a.date.localeCompare(b.date) || a.route.localeCompare(b.route));
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  const taxes = (cards.taxes || []).map((t) => ({ ...t, amount: Math.round(subtotal * t.rate * 100) / 100 }));
  return { customer, periodStart, lines, subtotal, taxes, total: subtotal + taxes.reduce((s, t) => s + t.amount, 0) };
};

/** A re-issued invoice (revision ≥ 1) is a "Final Invoice"; the first issue is a plain "Invoice". */
export const invoiceTitle = (inv) =>
  inv.revision ? `Final Invoice ${inv.number}${inv.revision > 1 ? ` (rev ${inv.revision})` : ''}` : `Invoice ${inv.number}`;

/** The live (non-superseded) invoice for a period + customer. */
export const currentInvoice = (invoices, periodStart, customer) =>
  invoices.find((i) => i.periodStart === periodStart && i.customer === customer && i.status !== 'superseded');

/** True when manifests were posted into the period after the invoice was issued. */
export const invoiceOutOfDate = (invoice, draft) =>
  !!invoice &&
  (draft.lines.length !== invoice.lines.length ||
    draft.lines.some((l) => !invoice.lines.some((x) => x.manifestId === l.manifestId)) ||
    Math.abs(draft.total - invoice.total) > 0.004);

/** Next INV-YYYY-NNNN number, sequential per calendar year. */
export const nextNumber = (prefix, existingNumbers, year = new Date().getFullYear()) => {
  const re = new RegExp(`^${prefix}-${year}-(\\d+)$`);
  const max = existingNumbers.reduce((m, n) => Math.max(m, Number((n || '').match(re)?.[1] || 0)), 0);
  return `${prefix}-${year}-${String(max + 1).padStart(4, '0')}`;
};
